import React, { createContext, useState, useEffect, useCallback } from "react";
import { Outlet } from "react-router-dom";
import { useAuthStore } from "../../stores/auth-store";
import { companyProductSystemUseCase } from "../../../di/container";
import type {
  CategoryDto,
  DeleteProductResult,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CreateProductRequest,
  UpdateProductRequest,
  DuplicateProductRequest,
} from "../../../domain/types/company/company-product-system";

interface ProductManagementContextValue {
  // 데이터
  categories: CategoryDto[];
  loading: boolean;
  error: string | null;

  // 데이터 새로고침
  refreshProducts: () => Promise<void>;

  // 카테고리 CRUD
  createCategory: (name: string) => Promise<CategoryDto | null>;
  updateCategory: (categoryId: number, name: string) => Promise<void>;
  toggleCategoryEnable: (categoryId: number, nextIsEnable: boolean) => Promise<void>;
  deleteCategory: (categoryId: number) => Promise<void>;

  // 카테고리 및 상품 순서 변경
  bulkUpdateCategoryOrder: (orders: Array<{ categoryId: number; displayOrder: number }>) => Promise<void>;
  bulkUpdateProductOrder: (orders: Array<{ productId: number; displayOrder: number }>) => Promise<void>;

  // 상품 생성
  createProduct: (request: CreateProductRequest) => Promise<number | null>;

  // 상품 이름 변경
  renameProduct: (productId: number, name: string) => Promise<void>;

  // 상품 복제
  duplicateProduct: (
    productId: number,
    request: DuplicateProductRequest,
  ) => Promise<number | null>;

  /**
   * 상품 삭제.
   *
   * 사용 중인 상품을 `confirmed` 없이 지우려 하면 삭제되지 않고 `deleted: false` 와
   * 사용처 요약이 돌아온다. 호출부는 그걸 경고 모달로 보여주고 `confirmed: true` 로 다시 부른다.
   */
  deleteProduct: (
    productId: number,
    confirmed?: boolean,
  ) => Promise<DeleteProductResult>;
}

export const ProductManagementContext =
  createContext<ProductManagementContextValue | null>(null);

export const ProductManagementWrapper: React.FC = () => {
  const { user } = useAuthStore();
  const companyId = user?.companyId;

  const [categories, setCategories] = useState<CategoryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 상품 목록 새로고침 (카테고리 + 소속 상품)
  const refreshProducts = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await companyProductSystemUseCase.getCategories(companyId);
      setCategories(data || []);
    } catch (err) {
      console.error("상품 목록 불러오기 실패:", err);
      setError("상품 목록을 불러오는데 실패했습니다.");
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  // 초기 데이터 로드
  useEffect(() => {
    if (companyId) {
      refreshProducts();
    }
  }, [companyId, refreshProducts]);

  // 카테고리 생성
  const createCategory = useCallback(
    async (name: string): Promise<CategoryDto | null> => {
      if (!companyId) return null;
      try {
        const request: CreateCategoryRequest = { name, isEnable: true };
        const created = await companyProductSystemUseCase.createCategory(companyId, request);
        await refreshProducts();
        return created || null;
      } catch (err) {
        console.error("카테고리 생성 실패:", err);
        throw err;
      }
    },
    [companyId, refreshProducts],
  );

  // 카테고리 수정
  const updateCategory = useCallback(
    async (categoryId: number, name: string): Promise<void> => {
      if (!companyId) return;
      try {
        const current = categories.find((c) => c.id === categoryId);
        const request: UpdateCategoryRequest = {
          name,
          isEnable: current?.isEnable ?? true,
        };
        await companyProductSystemUseCase.updateCategory(companyId, categoryId, request);
        await refreshProducts();
      } catch (err) {
        console.error("카테고리 수정 실패:", err);
        throw err;
      }
    },
    [companyId, categories, refreshProducts],
  );

  // 카테고리 활성화/비활성화 토글
  const toggleCategoryEnable = useCallback(
    async (categoryId: number, nextIsEnable: boolean): Promise<void> => {
      if (!companyId) return;
      try {
        const current = categories.find((c) => c.id === categoryId);
        if (!current) return;
        const request: UpdateCategoryRequest = {
          name: current.name,
          isEnable: nextIsEnable,
        };
        await companyProductSystemUseCase.updateCategory(companyId, categoryId, request);
        await refreshProducts();
      } catch (err) {
        console.error("카테고리 활성화 상태 변경 실패:", err);
        throw err;
      }
    },
    [companyId, categories, refreshProducts],
  );

  // 카테고리 삭제
  const deleteCategory = useCallback(
    async (categoryId: number): Promise<void> => {
      if (!companyId) return;
      try {
        await companyProductSystemUseCase.deleteCategory(companyId, categoryId);
        await refreshProducts();
      } catch (err) {
        console.error("카테고리 삭제 실패:", err);
        throw err;
      }
    },
    [companyId, refreshProducts],
  );

  // 카테고리 순서 대량 변경
  const bulkUpdateCategoryOrder = useCallback(
    async (orders: Array<{ categoryId: number; displayOrder: number }>): Promise<void> => {
      if (!companyId) return;
      try {
        await companyProductSystemUseCase.bulkUpdateCategoryOrder(companyId, orders);
        await refreshProducts();
      } catch (err) {
        console.error("카테고리 순서 변경 실패:", err);
        throw err;
      }
    },
    [companyId, refreshProducts],
  );

  // 상품 순서 대량 변경
  const bulkUpdateProductOrder = useCallback(
    async (orders: Array<{ productId: number; displayOrder: number }>): Promise<void> => {
      if (!companyId) return;
      try {
        await companyProductSystemUseCase.bulkUpdateProductOrder(companyId, orders);
        await refreshProducts();
      } catch (err) {
        console.error("상품 순서 변경 실패:", err);
        throw err;
      }
    },
    [companyId, refreshProducts],
  );

  // 상품 생성
  const createProduct = useCallback(
    async (request: CreateProductRequest): Promise<number | null> => {
      if (!companyId) return null;
      try {
        const created = await companyProductSystemUseCase.createProduct(companyId, request);
        await refreshProducts();
        return created?.id ?? null;
      } catch (err) {
        console.error("상품 생성 실패:", err);
        throw err;
      }
    },
    [companyId, refreshProducts],
  );

  // 상품 이름 변경
  const renameProduct = useCallback(
    async (productId: number, name: string): Promise<void> => {
      if (!companyId) return;
      try {
        const request: UpdateProductRequest = { name };
        await companyProductSystemUseCase.updateProduct(companyId, productId, request);
        await refreshProducts();
      } catch (err) {
        console.error("상품 이름 변경 실패:", err);
        throw err;
      }
    },
    [companyId, refreshProducts],
  );

  // 상품 복제
  const duplicateProduct = useCallback(
    async (
      productId: number,
      request: DuplicateProductRequest,
    ): Promise<number | null> => {
      if (!companyId) return null;
      try {
        const created = await companyProductSystemUseCase.duplicateProduct(
          companyId,
          productId,
          request,
        );
        await refreshProducts();
        return created?.id ?? null;
      } catch (err) {
        console.error("상품 복제 실패:", err);
        throw err;
      }
    },
    [companyId, refreshProducts],
  );

  // 상품 삭제
  const deleteProduct = useCallback(
    async (productId: number, confirmed = false): Promise<DeleteProductResult> => {
      if (!companyId) return { deleted: false, usages: [] };
      try {
        const result = await companyProductSystemUseCase.deleteProduct(
          companyId,
          productId,
          confirmed,
        );
        // 사용 중이라 보류된 경우엔 바뀐 게 없으니 목록을 다시 받지 않는다
        if (result.deleted) {
          await refreshProducts();
        }
        return result;
      } catch (err) {
        console.error("상품 삭제 실패:", err);
        throw err;
      }
    },
    [companyId, refreshProducts],
  );

  return (
    <ProductManagementContext.Provider
      value={{
        categories,
        loading,
        error,
        refreshProducts,
        createCategory,
        updateCategory,
        toggleCategoryEnable,
        deleteCategory,
        bulkUpdateCategoryOrder,
        bulkUpdateProductOrder,
        createProduct,
        renameProduct,
        duplicateProduct,
        deleteProduct,
      }}
    >
      <Outlet />
    </ProductManagementContext.Provider>
  );
};

// Custom Hook for using the context
export const useProductManagement = () => {
  const context = React.useContext(ProductManagementContext);
  if (!context) {
    throw new Error(
      "useProductManagement must be used within ProductManagementWrapper",
    );
  }
  return context;
};
